import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link as LinkIcon,
  Quote,
  RemoveFormatting,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react'
import { cn } from '../lib/shadcn/utils'
import './RichTextEditor.css'

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
  // When provided, an image button appears; the handler uploads the file and
  // returns a URL to embed (or null on failure).
  onImageUpload?: (file: File) => Promise<string | null>
}

// A lightweight, dependency-free WYSIWYG editor built on contenteditable +
// document.execCommand. Emits sanitizable HTML via onChange.
export default function RichTextEditor({ value, onChange, placeholder, className, onImageUpload }: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const savedRange = useRef<Range | null>(null)
  const [isEmpty, setIsEmpty] = useState(!value)
  const [uploadingImage, setUploadingImage] = useState(false)

  // Sync external value into the DOM only when it differs from what's rendered
  // (e.g. on reset), to avoid clobbering the caret while the user is typing.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (value !== el.innerHTML) {
      el.innerHTML = value
    }
    setIsEmpty(!el.textContent?.trim() && !el.querySelector('img,li,br'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const emit = useCallback(() => {
    const el = ref.current
    if (!el) return
    setIsEmpty(!el.textContent?.trim() && !el.querySelector('img,li,br'))
    onChange(el.innerHTML)
  }, [onChange])

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = ref.current
      if (!el) return
      el.focus()
      document.execCommand(command, false, arg)
      emit()
    },
    [emit]
  )

  const applyBlock = useCallback(
    (tag: string) => {
      // Toggle: if the current block already matches, revert to paragraph.
      exec('formatBlock', `<${tag}>`)
    },
    [exec]
  )

  const addLink = useCallback(() => {
    const el = ref.current
    if (!el) return
    const selection = window.getSelection()
    const hasSelection = selection && selection.toString().trim().length > 0
    const url = window.prompt('Link URL (https://…)')
    if (!url) return
    const safe = /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`
    el.focus()
    if (hasSelection) {
      document.execCommand('createLink', false, safe)
    } else {
      // No selection: insert the URL as a linked text node.
      document.execCommand('insertHTML', false, `<a href="${safe}">${safe}</a>`)
    }
    emit()
  }, [emit])

  // Save the caret position before the file dialog steals focus.
  const rememberSelection = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      savedRange.current = selection.getRangeAt(0).cloneRange()
    }
  }, [])

  const handleImageFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (imageInputRef.current) imageInputRef.current.value = ''
      if (!file || !onImageUpload) return
      if (!file.type.startsWith('image/')) return
      setUploadingImage(true)
      try {
        const url = await onImageUpload(file)
        if (!url) return
        const el = ref.current
        if (!el) return
        el.focus()
        // Restore the caret we saved before the picker opened.
        const selection = window.getSelection()
        if (savedRange.current && selection) {
          selection.removeAllRanges()
          selection.addRange(savedRange.current)
        }
        const safeAlt = file.name.replace(/"/g, '')
        document.execCommand('insertHTML', false, `<img src="${url}" alt="${safeAlt}" />`)
        emit()
      } finally {
        setUploadingImage(false)
      }
    },
    [onImageUpload, emit]
  )

  const btn = (
    key: string,
    Icon: typeof Bold,
    label: string,
    onClick: () => void
  ) => (
    <button
      key={key}
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={e => e.preventDefault()} // keep selection/focus in the editor
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  )

  return (
    <div className={cn('rounded-md border border-input bg-background', className)}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1">
        {btn('bold', Bold, 'Bold', () => exec('bold'))}
        {btn('italic', Italic, 'Italic', () => exec('italic'))}
        {btn('underline', Underline, 'Underline', () => exec('underline'))}
        {btn('strike', Strikethrough, 'Strikethrough', () => exec('strikeThrough'))}
        <span className="mx-1 h-5 w-px bg-border" />
        {btn('h1', Heading1, 'Heading 1', () => applyBlock('h1'))}
        {btn('h2', Heading2, 'Heading 2', () => applyBlock('h2'))}
        {btn('quote', Quote, 'Quote', () => applyBlock('blockquote'))}
        <span className="mx-1 h-5 w-px bg-border" />
        {btn('ul', List, 'Bullet list', () => exec('insertUnorderedList'))}
        {btn('ol', ListOrdered, 'Numbered list', () => exec('insertOrderedList'))}
        {btn('link', LinkIcon, 'Insert link', addLink)}
        {onImageUpload && (
          <button
            type="button"
            aria-label="Insert image"
            title="Insert image"
            disabled={uploadingImage}
            onMouseDown={e => {
              e.preventDefault()
              rememberSelection()
            }}
            onClick={() => imageInputRef.current?.click()}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
          >
            {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
          </button>
        )}
        <span className="mx-1 h-5 w-px bg-border" />
        {btn('clear', RemoveFormatting, 'Clear formatting', () => exec('removeFormat'))}
      </div>
      {onImageUpload && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => void handleImageFile(e)}
        />
      )}
      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-multiline="true"
        suppressContentEditableWarning
        data-empty={isEmpty}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        className="rte-content min-h-[160px] max-h-[320px] overflow-y-auto px-3 py-2 text-sm text-foreground"
      />
    </div>
  )
}
